<template lang="pug">
.custom-kneesleeve(ref='customKneesleeve' id="custom-sleeve")
  Suspense
    ProductPage(:product="product" :showTabsSlot="true" :showMainSlot="true")
      template(#media)
        Media(
          :gallery="gallery"
          layout="desktop"
          v-model="selectedImg"
          :product="product"
        )
          template(#zoomoverlay="data")
            custom-kneesleeve-zoom(v-if="data.idx === 0" @loaded="zoomViewVisible")
          template(#overlay="data")
            custom-kneesleeve-canvas(
              ref="canvasCtx"
              v-if="data.idx === 0"
              @loaded="canvasLoaded($event);"
              v-model:canvasState="canvasState"
              :buyRequest="buyRequest"
              :key="canvasKey"
            )
      template(#title="slotProps")
        ProductTitle(:product="product" :show-h1="slotProps.showH1")
        Price(:product="product" :buy-request="buyRequest" theme='rhpa')
      template(#tabs)
        RenderBlock(:block="slots.tabs" :product="product")
      template(#rhpa)
        .rhpa
          Message.error(status="error" v-if="!!error")
            Icon.left-icon(name="alert-error")
            | {{ __(error) }}
          ProductBadges.rhpa-badges(:buyRequest="buyRequest" )
          img.zeus-tool-logo(src='@/assets/zeus-custom.svg' alt="Zeus Tool" height="50" width="150")
          Accordion(:heading="'Select Size and Thickness'" left-icon="misc/ruler" theme="rhpa" closed-icon="arrow-up" open-icon="arrow-down")
            configuration(:buyRequest="buyRequest")
          Accordion(:heading="'Add Custom Design'" left-icon="misc/zeus-head" theme="rhpa" closed-icon="arrow-up" open-icon="arrow-down")
            custom-kneesleeve-options(v-model:canvasState="canvasState" ref="customRhpa" @loaded="rhpaLoaded=true" :key="rhpaKey")
          rhpa-footer(:buy-request="buyRequest" :disabled="false" :validate="createRecipe" )
      template(#flexible)
        RenderBlock(:block="slots.flexible" :product="product")
      template(#main)
        RenderBlock(:block="slots.main" :product="product")
      template(#full)
        RenderBlock(:block="slots.full" :product="product")
      template(#bottom)
        Product-bottom(:product="product")
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, useTemplateRef, watch, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import type { FabricObject } from 'fabric'
import CustomKneesleeveZoom from './custom-kneesleeve-zoom.vue'
import type { CanvasState } from './kneesleeve-types'
import { useProductStore } from '@/js/stores/productStore'
import CustomKneesleeveOptions from '@/js/pages/product/custom/custom-kneesleeve-options.vue'
import CustomKneesleeveCanvas from '@/js/pages/product/custom/custom-kneesleeve-canvas.vue'
import type { BuyRequest } from '@/js/services/RogueTypes'
import { useRogue } from '@/js/services/Rogue'

const ProductTitle = defineAsyncComponent(() => import('../../../components/rhpa/product-title.vue'))
const RhpaFooter = defineAsyncComponent(() => import('../../../components/rhpa/rhpa-footer.vue'))
const Rhpa = defineAsyncComponent(() => import('../../../components/rhpa/rhpa.vue'))
const ProductBadges = defineAsyncComponent(() => import('../../../components/rhpa/badges.vue'))
const ProductBottom = defineAsyncComponent(() => import('@/js/pages/product/product-bottom.vue'))

const props = defineProps<{
  buyRequest: BuyRequest,
}>()

const rogue = useRogue()
const route = useRoute()
const productStore = useProductStore()
const updateBuyRequest = inject('updateBuyRequest', (_val: BuyRequest) => { })

const canvasCtxRef = useTemplateRef<typeof CustomKneesleeveCanvas>('canvasCtx')
const customRhpaRef = useTemplateRef<typeof CustomKneesleeveOptions>('customRhpa')

const error = ref<string | null>(null)
const loaded = ref<boolean>(false)
const rhpaLoaded = ref<boolean>(false)
const rhpaKey = ref<number>(0)
const selectedImg = ref<number>(0)
const canvasKey = ref<number>(0)
const canvasState = ref<CanvasState>({
  tab                   : 'Upload Image',
  selectedColor         : 'white',
  userImages            : 0,
  userTexts             : 0,
  totalObjects          : 0,
  rImagePosition        : -1,
  customizationSnapshot : [],
  selectedItem          : {
    first    : false,
    last     : false,
    type     : '',
    img      : '',
    text     : '',
    size     : 0,
    font     : '',
    fill     : '',
    position : 0,
  },
})

const products = computed(() => productStore.products)
const product = computed(() => products.value[props.buyRequest.productId])
const slots = computed(() => ((product.value.roguebuilder || {}).slots || {}))
const simple = computed(() => products.value[props.buyRequest.children![0].productId] || {})
const recipe = computed(() => route.query?.rid || '')
const gallery = computed(() => {
  if (product.value.gallery && simple.value.gallery) {
    return [...simple.value.gallery, ...product.value.gallery.slice(1)]
  }

  return []
})

/* called once we are confident we have canvas context */
function handleScroll() {
  // #pdp-media comes from ProductPage inside a Suspense boundary, so it can still
  // be absent when a scroll event fires.
  const media = document.getElementById('pdp-media')
  if (!media) return

  const rect = media.getBoundingClientRect()
  if (rect.top <= 0) {
    media.classList.add('sticky-custom-kneesleeve')
  } else {
    media.classList.remove('sticky-custom-kneesleeve')
  }
}

function canvasLoaded() {
  customRhpaRef.value?.loadCanvas(canvasCtxRef.value!)
  loaded.value = true
}

function zoomViewVisible(el: HTMLImageElement) {
  const ctx = canvasCtxRef.value!.fabric.canvas
  if (ctx) {
    ctx.discardActiveObject()
    el.setAttribute('src', ctx.toDataURL({ quality: 1, format: 'jpg', enableRetinaScaling: true }))
  }
}

function mapCustomizations() {
  const customizations = []
  const textFields = canvasCtxRef.value!.fabric.canvas.getObjects().filter((obj: FabricObject) => obj?.text)
  textFields.forEach((text, index) => {
    customizations.push({
      key   : `kneesleeve-text-${index}`,
      type  : 'text',
      value : text.text,
    })
    customizations.push({
      key   : `kneesleeve-font-${index}`,
      type  : 'font',
      value : text.fontFamily,
    })
    customizations.push({
      key   : `kneesleeve-font-size-${index}`,
      type  : 'font',
      value : text.fontSize,
    })
    customizations.push({
      key   : `kneesleeve-font-color-${index}`,
      type  : 'font',
      value : text.fill,
    })
  })
  customizations.push({
    key   : 'thickness',
    type  : 'thickness',
    value : simple.value.attributes.sleeve_thickness,
  })
  customizations.push({
    key   : 'size',
    type  : 'size',
    value : simple.value.attributes.generic_size,
  })
  customizations.push({
    key   : 'selectedColor',
    type  : 'color',
    value : canvasCtxRef.value!.fabric.sleeveColor.value,
  })

  customizations.push({
    key   : 'color',
    type  : 'color',
    value : canvasCtxRef.value!.fabric.sleeveColor.value,
  })

  return customizations
}

async function createRecipe() {
  error.value = null
  const fabricObject = await canvasCtxRef.value!.generateFabricObject()
  const customizations = mapCustomizations()
  const objects = (canvasCtxRef.value!.fabric.canvas.getObjects() as FabricObject[])
    .map(obj => obj.toObject(['rogueImage', 'selectable', 'id', 'aCoords', 'fontFamily', 'url', 'clipPath', 'objType']))
  const images = objects.filter(value => value?.objType === 'image')

  const payload = {
    sku     : simple.value.sku,
    renders : [{
      key  : 'kneesleeve-render',
      path : fabricObject.thumbUrl,
      href : '',
    }],
    fabricConfig : fabricObject.json,
    stencils     : [{
      key  : 'kneesleeve-print',
      path : fabricObject.print,
      href : '',
    }],
    productId      : simple.value.productId,
    customizations : customizations,
  }

  images.forEach((img, index) => {
    payload.renders.push({ key: `user-image-${index}`, path: img.dataUrl, href: '' })
  })

  payload.fabricConfig.canvasState = canvasState.value

  try {
    const newRecipe = await rogue.fetch('/svc_zeus/recipes', 'POST', payload)
    const updatedBuyRequest = JSON.parse(JSON.stringify(props.buyRequest))
    updatedBuyRequest.metadata = {
      recipeId              : newRecipe.recipeId,
      nonFluidCustomization : true,
    }
    updateBuyRequest(updatedBuyRequest)

    return true
  } catch (e) {
    if (e.status === 413) {
      error.value = 'One or more photos are to big. Crop or remove the image and try again.'
    } else {
      error.value = 'There was an error creating your customization. Please try again later.'
    }
    rogue.log.error('Error creating recipe', e)
    canvasCtxRef.value!.generatePrintTemplate = false

    return false
  }
}

watch(recipe, () => {
  if (loaded.value) {
    canvasKey.value += 1
    rhpaKey.value += 1
  }
})

watch(canvasState, () => {
  selectedImg.value = 0
}, {
  deep: true,
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>
<style lang="scss" scoped>

.hundo {
  margin-top: $size-xxs
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .content {
    display: flex;
    align-items: center;
  }
}

.header-icon {
  height: $size-l;
  width: $size-xs;
  margin-right: $size-xxs;
}

.opt-group {
  &:first-child {
    border-top: 1px solid $color-gray;
  }

  border-bottom: 1px solid $color-gray;
  &.custom-design {
    margin-bottom: $space-s;
  }
}

.opt-group-head {
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  padding: $space-s 0;

  h5 {
    margin-bottom: 0;
  }
}

.error {
  margin: $space-s 0;
}

.configuration-area {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.content {
  display: flex;
  align-items: center;
}

@include mobile {
  #custom-sleeve {
    overflow-x: clip;
  }
}

:deep() {
  .grid-rhpa {
    z-index: 10;
  }

  // Override pdp media style if on mobile
  @include mobile {
    #pdp-media {
      margin-left: -160px;
      margin-right: -160px;
    }
  }
}

</style>

<style lang="scss">
@include mobile {
  .sticky-custom-kneesleeve {
    position: sticky;
    top: 0;
    z-index: 11;
    background: #f8f8f8;
  }
}
</style>
